import { Grid, Typography, Menu, IconButton, MoreHorizIcon, MenuItem, SettingsIcon, Button } from '@rbx/ui';
import React, { FunctionComponent, useCallback } from 'react';
import { useTranslation } from '@rbx/intl';
import { CreatorStreamNotificationsMarkAllCreatorStreamNotificationsReadOperationRequest } from '@rbx/clients/creatorNotificationStreamsApi';
import useTrayHeaderStyles from './TrayHeader.styles';
import useNavigationConfigs from '../hooks/useNavigationConfigs';
import {
  clickNotificationSettingsButtonEventModel,
  markAllNotificationsReadEventModel
} from '../event/eventConstants';
import { useNotificationClient } from './contexts/NotificationClientProvider';
import { User } from './types/user';
import CREATOR_HUB_NOTIFICATION_CHANNEL from './utils/CreatorHubNotificationChannel';
import { getCreatorHubBasePath } from '../utils/getBasePaths';
import TNotificationGroup from './types/TNotificationGroup';

type TTrayHeader = {
  notifications: TNotificationGroup[];
  newNotificationExists: boolean;
  user: User | null;
  markAllAsRead: () => void;
  menuAnchor: HTMLElement | null;
  setMenuAnchor: (el: HTMLElement | null) => void;
  setExpanded: (expanded: boolean) => void;
  onSettingsClick?: () => void;
  enableNotificationTrayV2?: boolean;
};

const settingsPath = '/settings/notifications';

const TrayHeader: FunctionComponent<TTrayHeader> = ({
  notifications,
  newNotificationExists,
  user,
  markAllAsRead,
  menuAnchor,
  setMenuAnchor,
  setExpanded,
  onSettingsClick,
  enableNotificationTrayV2 = false,
}) => {
  const { translate } = useTranslation();
  const { sendEvent, environment, target } = useNavigationConfigs();
  const { notificationClient } = useNotificationClient();
  const { classes: styles } = useTrayHeaderStyles();

  const clearMenuAnchorWithTimeout = useCallback(() => {
    setTimeout(() => {
      setMenuAnchor(null);
    });
  }, [setMenuAnchor]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setMenuAnchor(event.currentTarget);
    },
    [setMenuAnchor]
  );

  const handleClose = useCallback(() => {
    clearMenuAnchorWithTimeout();
  }, [clearMenuAnchorWithTimeout]);

  const handleSettingsClick = useCallback(() => {
    if (onSettingsClick) {
      onSettingsClick();
    } else {
      window.open(`${getCreatorHubBasePath(target, environment)}${settingsPath}`, '_self');
    }

    setExpanded(false);
    clearMenuAnchorWithTimeout();
    sendEvent(clickNotificationSettingsButtonEventModel({
      hasUnSeenNotifications: newNotificationExists,
      unreadNotificationCount: notifications.filter((notification) => !notification.titleNotification.read).length,
      notificationsCount: notifications.length,
    }));
  }, [clearMenuAnchorWithTimeout, environment, onSettingsClick, sendEvent, setExpanded, target, newNotificationExists, notifications]);

  const handleMarkAllAsReadClick = useCallback(() => {
    if (user?.id) {
      try {
        const args: CreatorStreamNotificationsMarkAllCreatorStreamNotificationsReadOperationRequest =
        {
          userId: user.id,
          creatorStreamNotificationsMarkAllCreatorStreamNotificationsReadRequest: {
            notificationChannel: CREATOR_HUB_NOTIFICATION_CHANNEL
          }
        };
        notificationClient?.creatorStreamNotificationsMarkAllCreatorStreamNotificationsRead(args);
      } catch (e) {
        // NOTE (@mbae, 03/22/24): We currently don't have a dedicated error state for mark all as read.
        // For now, we prevent the error from breaking the UI further since mark all as read
        // isn't critical for viewing notifications.
        // eslint-disable-next-line no-console
        console.error('Failed to mark all as read', e);
      }
      markAllAsRead();
      sendEvent(markAllNotificationsReadEventModel({
        hasUnSeenNotifications: newNotificationExists,
        unreadNotificationCount: notifications.filter((notification) => !notification.titleNotification.read).length,
        notificationsCount: notifications.length,
      }));
    }

    clearMenuAnchorWithTimeout();
  }, [user?.id, clearMenuAnchorWithTimeout, notificationClient, markAllAsRead, sendEvent, newNotificationExists, notifications]);

  return (
    <Grid className={styles.header}>
      <Typography variant='h6'>{translate('Heading.NotificationTray')}</Typography>
      {enableNotificationTrayV2 ? (
        <div>
          <Button
            size='small'
            variant='text'
            color='secondary'
            className={styles.markAllAsReadButton}
            onClick={handleMarkAllAsReadClick}
          >
            {translate('Action.MarkAllAsRead')}
          </Button>
          <IconButton
            aria-label={translate('Action.Settings')}
            // className={styles.closeButton}
            onClick={handleSettingsClick}>
            <SettingsIcon color='secondary' />
          </IconButton>
        </div>) : (
          // Note @neoxu: Remove after notification tray v2 is fully released
        <React.Fragment>
          <IconButton
            aria-label={`${translate('Label.NotificationMenuAria')}`}
            className={styles.iconButton}
            onClick={handleClick}>
            <MoreHorizIcon fontSize='small' color='secondary' />
          </IconButton>
          <Menu
            id='options'
            variant='modal'
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
            MenuListProps={{
              'aria-labelledby': `${translate('Label.NotificationMenuAria')}`
            }}>
            <MenuItem variant='standardMenu' onClick={handleMarkAllAsReadClick}>
              <Typography variant='body1' color='primary'>
                {translate('Action.MarkAllAsRead')}
              </Typography>
            </MenuItem>
            <MenuItem variant='standardMenu' onClick={handleSettingsClick}>
              <Typography variant='body1' color='primary'>
                {translate('Action.Settings')}
              </Typography>
            </MenuItem>
          </Menu>
        </React.Fragment>)}
    </Grid>
  );
};

export default TrayHeader;
